import { gql, useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/react';
import { Box, Flex } from '@theme-ui/components';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { Button } from '@app/components/Button';
import CheckoutBackLink from '@app/components/CheckoutBackLink';
import FormattedMoney from '@app/components/FormattedMoney';
import { RadioBoxLabel, RadioBoxRadio } from '@app/components/RadioBox';
import { H2 } from '@app/components/typography';
import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';
import { getEnabledNotices, stripHTML } from '@app/helpers/shop';
import { pushPerpetualEcommerceEvent } from '@app/helpers/tracking';
import { useFireOnce } from '@app/hooks/useFireOnce';
import useGTMTracking from '@app/hooks/useGTMTracking';
import useRouterPush from '@app/hooks/useRouterPush';
import useSimpleCart from '@app/hooks/useSimpleCart';
import messages from '@app/pages/Checkout/Delivery/Delivery.messages';
import {
  DeliveryError,
  Notice,
} from '@app/pages/Checkout/Delivery/Delivery.styles';
import type {
  AvailableShippingMethodsQuery_magento2_customerCart_shippingAddresses_availableShippingMethods as AvailableShippingMethods,
  AvailableShippingMethodsQuery,
} from '@app/pages/Checkout/Delivery/__generated__/AvailableShippingMethodsQuery';
import type {
  SetShippingMethod,
  SetShippingMethodVariables,
} from '@app/pages/Checkout/Delivery/__generated__/SetShippingMethod';
import { rem } from '@app/styles/theme/layout';

const Delivery = () => {
  const push = useRouterPush();
  const { language = 'en' } = useParams<{ language?: string }>();
  const DELIVERY_TIMES_URL = `https://www.native-instruments.com/${language}/shop-information/shipping-fees-payment/#delivery_times`;
  const { cartId } = useGetCartId();
  const { storeConfig, appliedCoupons, items, prices } = useSimpleCart();
  const fireEventOnce = useFireOnce();

  useGTMTracking({ step: 'delivery', items, prices, storeConfig });

  useEffect(() => {
    fireEventOnce(() => {
      pushPerpetualEcommerceEvent('add_shipping_method', {
        currency: prices?.grandTotal?.currency,
        value: prices?.grandTotal?.value,
        coupons: appliedCoupons,
        items: items?.map((item) => ({
          // item_id/item_name should come from the parent product,
          // but it's not available in the cart item data yet.
          // @TODO [ECOM-85, ECOM-84]
          item_id: item?.product?.sku,
          item_name: item?.product?.name,
          sku_id: item?.product?.sku,
          sku_name: item?.product?.name,
          sdbs_id: item?.product?.sdbsId,
          price: item?.prices?.price.value || 0,
          quantity: item?.quantity,
        })),
      });
    });
  }, [prices, items, fireEventOnce]);

  const [selectedMethod, setSelectedMethod] =
    useState<AvailableShippingMethods>(methodInitialState);
  const [error, setError] = useState(false);
  const includingTax = storeConfig?.taxSalesDisplaySubtotal === 'including';

  const { data, loading: methodsLoading } =
    useQuery<AvailableShippingMethodsQuery>(GET_SHIPPING_METHODS, {
      fetchPolicy: 'network-only',
      ssr: false,
      onCompleted: (data) => {
        const shippingMethods =
          data?.magento2?.customerCart?.shippingAddresses[0]
            ?.availableShippingMethods;
        const cheapestMethod = getCheapestShippingMethod(shippingMethods);
        setSelectedMethod(cheapestMethod);
      },
    });

  const [setShippingMethod, { loading: isShippingMethodSending }] = useMutation<
    SetShippingMethod,
    SetShippingMethodVariables
  >(SET_SHIPPING_METHOD, {
    onCompleted: () => {
      push('/:language/shop/checkout/review/');
    },
    onError: () => {
      setError(true);
    },
  });

  const shippingMethods =
    data?.magento2?.customerCart?.shippingAddresses[0]
      ?.availableShippingMethods;

  const handleSubmit = useCallback(async () => {
    if (selectedMethod) {
      await setShippingMethod({
        variables: {
          cartId: cartId || '',
          carrierCode: selectedMethod.carrierCode,
          methodCode: selectedMethod.methodCode || '',
        },
      });
    }
  }, [cartId, selectedMethod, setShippingMethod]);

  if (methodsLoading) {
    return null;
  }

  const notices = getEnabledNotices(data);
  const notice = stripHTML(notices?.[0]?.content);

  return (
    <>
      <H2 as="h1">
        <FormattedMessage {...messages.title} />
      </H2>
      {notice && (
        <Box mt={4}>
          <Notice role="complementary">{notice}</Notice>
        </Box>
      )}
      <Box mt={4}>
        <FormattedMessage
          {...messages.deliveryTimesMessage}
          values={{
            deliveryTimesLink: (
              <a
                data-testid="delivery-times-link"
                target="_blank"
                rel="noreferrer"
                css={css`
                  font-weight: 500;
                `}
                href={DELIVERY_TIMES_URL}
              >
                <FormattedMessage {...messages.deliveryTimesLink} />
              </a>
            ),
          }}
        />
      </Box>
      <form>
        <Flex my={4} sx={{ flexWrap: 'wrap', justifyContent: 'space-between' }}>
          {' '}
          {error && (
            <Box mb={1} sx={{ width: '100%' }}>
              <DeliveryError>
                <span data-testid="delivery-method-error">
                  <FormattedMessage
                    {...messages.error}
                    data-testid="delivery-method-error"
                  />
                </span>
              </DeliveryError>
            </Box>
          )}
          {shippingMethods?.map(
            (method) =>
              method && (
                <Box
                  key={method.carrierCode}
                  sx={{ width: '100%' }}
                  css={css`
                    &:not(:last-of-type) {
                      margin-bottom: ${rem(15)};
                    }
                  `}
                >
                  <RadioBoxRadio
                    data-testid={`shipping-method-${method.carrierCode}`}
                    id={method.carrierCode}
                    value={method.carrierCode}
                    checked={selectedMethod.carrierCode === method.carrierCode}
                    onChange={() => setSelectedMethod(method)}
                    disabled={methodsLoading}
                    outlineChecked
                  />
                  <RadioBoxLabel
                    htmlFor={method.carrierCode}
                    data-testid={`shipping-method-${method.carrierCode}-title`}
                  >
                    {method.carrierTitle} (
                    <span
                      data-testid={`shipping-method-${method.carrierCode}-price`}
                    >
                      <FormattedMoney
                        {...method[
                          includingTax ? 'priceInclTax' : 'priceExclTax'
                        ]}
                      />
                    </span>
                    )
                  </RadioBoxLabel>
                </Box>
              ),
          )}
        </Flex>
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <CheckoutBackLink to="/:language/shop/checkout/address">
            <FormattedMessage {...messages.back} />
          </CheckoutBackLink>
          <Button
            type="primary"
            isLoading={isShippingMethodSending}
            data-testid="delivery-continue-button"
            onClick={handleSubmit}
          >
            <FormattedMessage {...messages.continue} />
          </Button>
        </Flex>
      </form>
    </>
  );
};

export default Delivery;

const getCheapestShippingMethod = (
  shippingMethods?: (AvailableShippingMethods | null)[] | null,
): AvailableShippingMethods => {
  let lowest = Number.POSITIVE_INFINITY;
  let cheapestShippingMethod;
  shippingMethods?.forEach((shippingMethod) => {
    if (shippingMethod) {
      const value = shippingMethod?.priceInclTax?.value;
      if (typeof value === 'number' && Number.isFinite(value)) {
        if (value < lowest) {
          lowest = value;
          cheapestShippingMethod = shippingMethod;
        }
      }
    }
  });

  return cheapestShippingMethod;
};

const methodInitialState: AvailableShippingMethods = {
  __typename: 'Magento2_AvailableShippingMethod',
  carrierTitle: '',
  carrierCode: '',
  methodCode: null,
  methodTitle: null,
  priceExclTax: { __typename: 'Magento2_Money', value: null, currency: null },
  priceInclTax: { __typename: 'Magento2_Money', value: null, currency: null },
};

export const GET_SHIPPING_METHODS = gql`
  query AvailableShippingMethodsQuery {
    magento2 {
      customerCart {
        id
        shippingAddresses {
          availableShippingMethods {
            priceExclTax {
              currency
              value
            }
            priceInclTax {
              currency
              value
            }
            carrierTitle
            carrierCode
            methodTitle
            methodCode
          }
        }
      }
      cmsBlocks(identifiers: ["shipping-notice"]) {
        items {
          title
          identifier
          content
        }
      }
    }
  }
`;

export const SET_SHIPPING_METHOD = gql`
  mutation SetShippingMethod(
    $cartId: String!
    $carrierCode: String!
    $methodCode: String!
  ) {
    magento2 {
      setShippingMethodsOnCart(
        input: {
          cart_id: $cartId
          shipping_methods: [
            { carrier_code: $carrierCode, method_code: $methodCode }
          ]
        }
      ) {
        cart {
          id
        }
      }
    }
  }
`;
